import { t as tr } from '../i18n.js'
import { Fragment, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { api, type AuditPage, type AuditRow } from '../api.js'
import { fmt } from './Account.js'

const ENTITY_LABEL: Record<string, string> = {
  users: 'user', projects: 'project', portfolios: 'portfolio', statuses: 'status', milestones: 'milestone',
  tasks: 'task', time_entries: 'time entry', invites: 'invite', project_members: 'membership', approval_periods: 'approval',
}
const verb = (r: AuditRow) => {
  if (r.entityType === 'project_members') return r.action === 'insert' ? 'added to project' : r.action === 'delete' ? 'removed from project' : 'changed role for'
  if (r.action === 'update' && r.changed?.length === 1 && r.changed[0] === 'deleted_at') return (r.after?.deleted_at ? 'deleted' : 'restored')
  if (r.action === 'update' && r.changed?.length === 1 && r.changed[0] === 'archived_at') return (r.after?.archived_at ? 'archived' : 'unarchived')
  if (r.action === 'update' && r.changed?.length === 1 && r.changed[0] === 'status_id') return 'moved'
  return { insert: 'created', update: 'updated', delete: 'deleted' }[r.action]
}
const fmtVal = (v: unknown) => v === null || v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)

/**
 * Shared audit list: the admin console passes `/admin/audit`, a project page
 * passes `/projects/:id/activity`. Cursor-paginated ("Load more").
 */
export function AuditList({ endpoint, filters, compact }: { endpoint: string; filters?: Record<string, string>; compact?: boolean }) {
  const qs = new URLSearchParams(Object.entries(filters ?? {}).filter(([, v]) => v)).toString()
  const q = useInfiniteQuery({
    queryKey: ['audit', endpoint, qs],
    queryFn: ({ pageParam }) => api<AuditPage>(`${endpoint}?${qs}${qs ? '&' : ''}limit=${compact ? 20 : 50}${pageParam ? `&before=${pageParam}` : ''}`),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.nextBefore,
  })
  const [open, setOpen] = useState<number | null>(null)
  const rows = q.data?.pages.flatMap((p) => p.rows) ?? []
  return (
    <div className="stack">
      {q.isPending && <p className="muted small">{tr("Loading…")}</p>}
      {rows.length === 0 && q.data && <p className="muted small">{tr("Nothing recorded yet.")}</p>}
      {rows.length > 0 && (
        <table className="table audit">
          <thead><tr><th>{tr("When")}</th><th>{tr("Who")}</th><th>{tr("What")}</th>{!compact && <th>{tr("Project")}</th>}<th></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.id}>
                <tr className={open === r.id ? 'selected' : ''}>
                  <td className="small mono" style={{ whiteSpace: 'nowrap' }}>{fmt(r.at)}</td>
                  <td>{r.actorName ?? <span className="muted">{tr("system")}</span>}{r.via && <span className="small muted"> · {tr("via API token")} {r.viaName ?? r.via.slice(0, 8)}</span>}</td>
                  <td>{verb(r)} <span className="muted">{ENTITY_LABEL[r.entityType] ?? r.entityType}</span> <strong>{r.label ?? r.entityId.slice(0, 8)}</strong>
                    {r.action === 'update' && r.changed && r.changed.length > 0 && <span className="small muted"> · {r.changed.join(', ')}</span>}</td>
                  {!compact && <td className="small">{r.projectCode ?? ''}</td>}
                  <td>{(r.before || r.after) && <button className="link small" onClick={() => setOpen(open === r.id ? null : r.id)}>{open === r.id ? 'hide' : 'details'}</button>}</td>
                </tr>
                {open === r.id && (
                  <tr><td colSpan={compact ? 4 : 5}>
                    <table className="table small diff">
                      <thead><tr><th>{tr("Field")}</th><th>{tr("Before")}</th><th>{tr("After")}</th></tr></thead>
                      <tbody>
                        {Array.from(new Set([...Object.keys(r.before ?? {}), ...Object.keys(r.after ?? {})])).sort().map((k) => (
                          <tr key={k}><td className="mono">{k}</td><td className="mono">{fmtVal(r.before?.[k])}</td><td className="mono">{fmtVal(r.after?.[k])}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
      {q.hasNextPage && <div><button className="button" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>Load more</button></div>}
    </div>
  )
}
